import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import FinanceShell from "@/components/finance/FinanceShell";
import Pagination from "@/components/Pagination";
import { formatTRY, formatDate } from "@/components/finance/financeLabels";
import { FileText, Search } from "lucide-react";

const PAGE_SIZE = 50;
const STATUS_LABELS = { draft: "Taslak", issued: "Kesildi", sent: "Gönderildi", paid: "Ödendi", cancelled: "İptal" };
const STATUS_COLORS = { draft: "bg-slate-100 text-slate-700", issued: "bg-sky-100 text-sky-700", sent: "bg-violet-100 text-violet-700", paid: "bg-emerald-100 text-emerald-700", cancelled: "bg-rose-100 text-rose-700" };

export default function InvoicesPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE });
            if (q) params.set("q", q);
            if (status) params.set("status", status);
            const r = await api.get(`/admin/finance/invoices?${params.toString()}`);
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
        } finally { setLoading(false); }
    };
    useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, status, page]);
    useEffect(() => { setPage(1); }, [q, status]);

    return (
        <FinanceShell>
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Fatura no veya cari..." className="w-full pl-9 pr-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm" data-testid="invoice-search" />
                </div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm" data-testid="invoice-status">
                    <option value="">Tüm Durumlar</option>
                    {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
            </div>

            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                {loading ? <div className="py-10 text-center text-slate-500">Yükleniyor...</div> : items.length === 0 ? (
                    <div className="py-12 text-center text-slate-500">
                        Henüz fatura yok. Faturalar rezervasyon onaylandığında otomatik kesilir veya manuel oluşturulabilir.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="invoices-table">
                            <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left">No</th>
                                    <th className="px-3 py-2 text-left">Tarih</th>
                                    <th className="px-3 py-2 text-left">Tip</th>
                                    <th className="px-3 py-2 text-left">Cari</th>
                                    <th className="px-3 py-2 text-right">KDV</th>
                                    <th className="px-3 py-2 text-right">Toplam</th>
                                    <th className="px-3 py-2 text-center">Durum</th>
                                    <th className="px-3 py-2 text-center">PDF</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((inv) => (
                                    <tr key={inv.id} className="border-t border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30" data-testid={`invoice-row-${inv.invoice_no}`}>
                                        <td className="px-3 py-2 font-mono text-xs font-bold">{inv.invoice_no}</td>
                                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(inv.date)}</td>
                                        <td className="px-3 py-2 text-xs uppercase">{inv.type}</td>
                                        <td className="px-3 py-2 font-semibold">{inv.party_name}</td>
                                        <td className="px-3 py-2 text-right">{formatTRY(inv.vat_amount)}</td>
                                        <td className="px-3 py-2 text-right font-bold">{formatTRY(inv.total)}</td>
                                        <td className="px-3 py-2 text-center"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_COLORS[inv.status]}`}>{STATUS_LABELS[inv.status]}</span></td>
                                        <td className="px-3 py-2 text-center">
                                            <a href={`/api/admin/finance/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" className="text-sky-600 hover:underline inline-flex items-center gap-1" data-testid={`invoice-pdf-${inv.invoice_no}`}>
                                                <FileText className="h-3.5 w-3.5" /> İndir
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <Pagination total={total} page={page} pageSize={PAGE_SIZE} onChange={setPage} label="fatura" />
            </div>
        </FinanceShell>
    );
}
